/** @jsxImportSource @emotion/react */
import { EraUploadSummaryContent, eraImportHeadline } from 'components/lab/billingEraReports/EraUploadSummaryModal'
import {
  BulkEraUploadItem,
  BulkEraUploadOutcome,
  bulkEraUploadOutcome,
} from 'components/lab/billingEraReports/eraUpload'
import { useEffect, useMemo, useState } from 'react'
import { Button, Modal, ProgressBar, Spinner } from 'react-bootstrap'
import { EraImportStatus } from 'store/types'

type Props = {
  show: boolean
  items: BulkEraUploadItem[]
  running: boolean
  onStop: () => void
  onHide: () => void
}

type Tone = { background: string; text: string }

const GREEN: Tone = { background: '#e6f4ea', text: '#1e6b34' }
const AMBER: Tone = { background: '#fff4e5', text: '#8a5300' }
const GREY: Tone = { background: '#f1f3f5', text: '#495057' }
const RED: Tone = { background: '#fdecea', text: '#a8321e' }

const OUTCOMES: Record<BulkEraUploadOutcome, { label: string; tone: Tone; icon: string }> = {
  imported: { label: 'Imported', tone: GREEN, icon: 'fas fa-check-circle' },
  partial: { label: 'Partially imported', tone: AMBER, icon: 'fas fa-exclamation-circle' },
  duplicate: { label: 'Already uploaded', tone: GREY, icon: 'far fa-copy' },
  empty: { label: 'No payments', tone: GREY, icon: 'far fa-file' },
  failed: { label: 'Failed', tone: RED, icon: 'fas fa-times-circle' },
  pending: { label: 'Waiting', tone: GREY, icon: 'far fa-clock' },
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`

// What happened to one file, in a line.
const describeItem = (item: BulkEraUploadItem): string => {
  if (item.state === 'waiting') return 'Waiting'
  if (item.state === 'uploading') return 'Uploading…'
  if (item.state === 'stopped') return 'Not uploaded — stopped'
  if (item.state === 'failed' || !item.summary) return item.error || 'Failed'

  const { summary } = item

  switch (summary.status) {
    case EraImportStatus.PARTIALLY_IMPORTED:
      return `${plural(summary.importedClaims, 'claim')} imported, ${summary.skippedClaims} already uploaded`
    case EraImportStatus.DUPLICATE:
      return summary.duplicateOfFilename
        ? `Already uploaded as ${summary.duplicateOfFilename}`
        : 'Every payment was already uploaded'
    case EraImportStatus.EMPTY:
      return 'No payments in this file'
    default:
      return `${plural(summary.importedTransactions, 'payment')} · ${plural(summary.importedClaims, 'claim')} imported`
  }
}

// A file whose import skipped something has a story worth opening.
const hasDetails = (item: BulkEraUploadItem): boolean => !!item.summary
  && (item.summary.status !== EraImportStatus.IMPORTED || !!item.summary.skippedTransactions?.length)

const Chip = ({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string
  count: number
  tone: Tone
  active: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    css={{
      border: `1px solid ${active ? tone.text : 'transparent'}`,
      borderRadius: 12,
      padding: '2px 10px',
      fontSize: 13,
      fontWeight: 600,
      background: tone.background,
      color: tone.text,
      cursor: 'pointer',
    }}>
    {label} · {count}
  </button>
)

const StateIcon = ({ item }: { item: BulkEraUploadItem }) => {
  if (item.state === 'uploading') {
    return <Spinner animation="border" size="sm" css={{ width: 14, height: 14, borderWidth: 2 }} />
  }

  if (item.state === 'stopped') {
    return <i className="fas fa-ban" css={{ color: GREY.text }} />
  }

  const outcome = OUTCOMES[bulkEraUploadOutcome(item)]

  return <i className={outcome.icon} css={{ color: outcome.tone.text }} />
}

/**
 * Several ERA files uploaded at once: how far the batch got, how many files
 * landed each way, and one line per file. A file that skipped something opens
 * the same breakdown a single upload shows. The batch keeps going when the
 * dialog is hidden.
 */
export const BulkEraUploadModal = ({ show, items, running, onStop, onHide }: Props) => {
  const [filter, setFilter] = useState<BulkEraUploadOutcome | null>(null)
  const [detailsKey, setDetailsKey] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)

  useEffect(() => {
    if (!running) setStopping(false)
  }, [running])

  const processed = items.filter((item) => item.state === 'done' || item.state === 'failed').length
  const counts = useMemo(() => items.reduce<Partial<Record<BulkEraUploadOutcome, number>>>((acc, item) => {
    if (item.state === 'stopped') return acc
    const outcome = bulkEraUploadOutcome(item)
    acc[outcome] = (acc[outcome] || 0) + 1
    return acc
  }, {}), [items])

  const visible = filter ? items.filter((item) => item.state !== 'stopped' && bulkEraUploadOutcome(item) === filter) : items
  const details = items.find((item) => item.key === detailsKey && item.summary)

  // From a file's details, closing (the button or Esc) goes back to the list
  // of files; only the list closes the dialog.
  const close = () => {
    if (details) {
      setDetailsKey(null)
      return
    }

    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={close}
      size="lg"
      centered
      css={{ '.modal-content': { borderRadius: 15 }, '.modal-body': { padding: '20px 30px' } }}>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 20 }}>
          {running ? `Uploading ERA files — ${processed} of ${items.length}` : 'ERA files uploaded'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {details?.summary ? (
          <>
            <button type="button" className="btn btn-link p-0 mb-2" onClick={() => setDetailsKey(null)}>
              <i className="fas fa-arrow-left mr-1" /> All files
            </button>
            <div css={{ fontWeight: 600, marginBottom: 10 }}>{eraImportHeadline(details.summary).title}</div>
            <EraUploadSummaryContent summary={details.summary} />
          </>
        ) : (
          <>
            <ProgressBar
              now={processed}
              max={Math.max(items.length, 1)}
              variant={counts.failed ? 'warning' : 'success'}
              css={{ height: 6, marginBottom: 14 }}
            />
            <div className="d-flex flex-wrap" css={{ gap: 8, marginBottom: 12 }}>
              <Chip label="All" count={items.length} tone={GREY} active={!filter} onClick={() => setFilter(null)} />
              {(Object.keys(OUTCOMES) as BulkEraUploadOutcome[])
                .filter((outcome) => counts[outcome])
                .map((outcome) => (
                  <Chip
                    key={outcome}
                    label={OUTCOMES[outcome].label}
                    count={counts[outcome] || 0}
                    tone={OUTCOMES[outcome].tone}
                    active={filter === outcome}
                    onClick={() => setFilter(filter === outcome ? null : outcome)}
                  />
                ))}
            </div>
            {/* Scrolls on its own: a batch can be hundreds of files. */}
            <div css={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #eef0f2', borderRadius: 6 }}>
              {visible.map((item) => (
                <div
                  key={item.key}
                  className="d-flex align-items-center"
                  css={{
                    gap: 12,
                    padding: '7px 12px',
                    fontSize: 13,
                    borderBottom: '1px solid #f1f3f5',
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                  <span css={{ width: 16, textAlign: 'center' }}>
                    <StateIcon item={item} />
                  </span>
                  <span
                    title={item.filename}
                    css={{
                      flex: '0 0 38%',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {item.filename}
                  </span>
                  <span
                    title={describeItem(item)}
                    className={item.state === 'failed' ? '' : 'text-muted'}
                    css={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: item.state === 'failed' ? RED.text : undefined,
                    }}>
                    {describeItem(item)}
                  </span>
                  {hasDetails(item) && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      css={{ fontSize: 13, whiteSpace: 'nowrap' }}
                      onClick={() => setDetailsKey(item.key)}>
                      Details
                    </button>
                  )}
                </div>
              ))}
              {!visible.length && <div className="text-muted p-3" css={{ fontSize: 13 }}>No files here.</div>}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {running && (
          <Button
            variant="outline-secondary"
            disabled={stopping}
            onClick={() => {
              setStopping(true)
              onStop()
            }}>
            {stopping ? 'Stopping…' : 'Stop after this file'}
          </Button>
        )}
        <Button variant="primary" onClick={close}>
          {details ? 'Back to all files' : running ? 'Hide — keep uploading' : 'Close'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
